import type { ReactNode } from 'react'

import { cn } from '@/shared/lib'

type SegmentedOption = {
  value: string
  label: ReactNode
}

type SegmentedProps = {
  options: SegmentedOption[]
  value: string
  onValueChange: (value: string) => void
  size?: 'sm' | 'md'
  /** Names the group for screen readers — «Плотность», «Период». */
  label: string
  className?: string
}

/**
 * A sunken tray of two to five mutually exclusive options — density, period,
 * view mode. The chosen segment lifts on `surface-3` with a 1px shadow; it is
 * the one place in the system where a shadow appears on a content surface,
 * because the lift is what reads as "pressed in".
 *
 * Semantically a radio group: arrow keys move between options, and only the
 * chosen one is in the tab order.
 */
export function Segmented({
  options,
  value,
  onValueChange,
  size = 'md',
  label,
  className,
}: SegmentedProps) {
  const isSmall = size === 'sm'

  return (
    <div
      role="radiogroup"
      aria-label={label}
      data-slot="segmented"
      className={cn(
        'inline-flex border border-veil/7 bg-black/28 p-0.75',
        'rounded-full',
        className,
      )}
    >
      {options.map((option) => {
        const isOn = option.value === value

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isOn}
            tabIndex={isOn ? 0 : -1}
            onClick={() => {
              onValueChange(option.value)
            }}
            className={cn(
              // The press and the chosen segment's lift are different cues on
              // purpose: the first is momentary and belongs to the finger, the
              // second persists and belongs to the state.
              'press font-medium whitespace-nowrap',
              isSmall ? 'rounded-full px-3 py-1.25 text-caption' : 'rounded-full px-4 py-1.5 text-body',
              isOn
                ? 'bg-surface-3 text-foreground shadow-[0_1px_3px_rgb(0_0_0/0.3)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
